import { fold } from './normalize';

/**
 * Match a name written in one script against the same name written in the other.
 *
 * The case this exists for: the interface is Arabic, the project is called
 * "Fawz", and either the Arabic recogniser hands back «فوز» or the reader simply
 * types it on an Arabic keyboard. Compared as strings those two share not one
 * character, so ordinary matching can never connect them however well it folds.
 *
 * The trick is that Arabic script is essentially consonantal, and Latin
 * transliterations of Arabic names disagree about vowels far more than about
 * consonants — Fawz / Fauz / Foze, محمد as Mohamed / Muhammad / Mohammad. Reduce
 * both sides to their consonant skeleton and the disagreement disappears.
 *
 * `w` and `y` are dropped along with the vowels, because و and ي do double duty as
 * long vowels: "Trial" keeps `trl` while «ترايل» would otherwise keep `tryl`.
 *
 * This is a fallback, never the primary pass. Skeletons collide readily — `fz`
 * is a small space — so `searchIndex` only reaches for it when an exact search
 * found nothing, compares whole tokens rather than substrings, and ranks whatever
 * it finds below every real match.
 */

/** Arabic letters to the Latin consonant they are usually written as. Letters
 *  that carry no consonant of their own — alef, ayn, hamza — map to nothing. */
const ARABIC_TO_LATIN: Readonly<Record<string, string>> = {
  ا: '', ب: 'b', ت: 't', ث: 't', ج: 'j', ح: 'h', خ: 'h', د: 'd', ذ: 'z',
  ر: 'r', ز: 'z', س: 's', ش: 's', ص: 's', ض: 'd', ط: 't', ظ: 'z', ع: '',
  غ: 'g', ف: 'f', ق: 'q', ك: 'k', ل: 'l', م: 'm', ن: 'n', ه: 'h', و: 'w',
  ي: 'y', ء: '', ى: 'y', ة: 'h', پ: 'p', چ: 'c', ژ: 'z', گ: 'g',
};

/** Dropped from both sides: the five vowels, plus the two letters that are vowels
 *  as often as they are consonants. */
const DROPPED = /[aeiouwy]/gu;

/** Any Arabic letter, i.e. which side of the script divide a string sits on. */
const ARABIC_SCRIPT = /\p{Script=Arabic}/u;

/** One token's skeleton, or '' when nothing consonantal survives. */
function skeletonOf(token: string): string {
  let latin = '';
  for (const char of token) {
    const mapped = ARABIC_TO_LATIN[char];
    // A character absent from the map is either already Latin or punctuation;
    // letters and digits pass through, everything else is dropped.
    latin += mapped ?? (/[\p{L}\p{N}]/u.test(char) ? char : '');
  }
  // Collapse doubles last: gemination is a shadda in Arabic and a doubled letter
  // in Latin, so محمد is `mhmd` while "Muhammad" would otherwise be `mhmmd`.
  return latin.replace(DROPPED, '').replace(/(.)\1+/gu, '$1');
}

/**
 * The consonant skeletons of `text`, one per word.
 *
 * Tokens shorter than two consonants are dropped: a one-letter skeleton matches
 * far too much to be evidence of anything.
 */
export function skeletonTokens(text: string): readonly string[] {
  return fold(text)
    .text.split(/[^\p{L}\p{N}]+/u)
    .map(skeletonOf)
    .filter((token) => token.length >= 2);
}

/**
 * True when the two texts are in DIFFERENT scripts and share a token skeleton.
 *
 * The differing-script requirement is what keeps this honest. Skeletons are a
 * blunt instrument, and allowing them to compare two Latin strings would quietly
 * relax the rule that every term must match: "first accounting" would find a
 * sitting called "First pass" on the strength of one shared word. Bridging
 * scripts is a job ordinary matching genuinely cannot do; being fuzzy within one
 * script is a different feature, and not this one.
 */
export function sharesSkeleton(query: string, candidate: string): boolean {
  if (ARABIC_SCRIPT.test(query) === ARABIC_SCRIPT.test(candidate)) return false;

  const wanted = new Set(skeletonTokens(query));
  if (wanted.size === 0) return false;
  return skeletonTokens(candidate).some((token) => wanted.has(token));
}
